import { STAGE } from '../stage.js';
import { packageExtras } from './format.js';
import { SCENERY, decodeScenery, emptyScenery, markScenery, } from './measure.js';
import { propBox } from './props.js';
import { lookup } from './util.js';
/**
 * A package's scenery as lesson checks read it (ADR-061): the grid the worker measured with the land
 * cut out, plus what the package says stands or moves there -- its props, the lanes its routes drive,
 * the reach of its turning parts -- as the sight profile adds its props (sight.ts).
 */
/** How far above the land a cell must reach to be scenery and not the land itself, metres. */
export const LAND_SLACK = 0.25;
const cellCentre = (i) => -SCENERY.half + (i + 0.5) * SCENERY.cell;
/**
 * `grid` with the land cut out: a cell whose top is within `LAND_SLACK` of the highest land under it
 * (its corners and its centre, `height`) holds only land -- a field's slope, a dune -- and is emptied,
 * in each layer.
 */
export function sceneryAboveLand(grid, height) {
    const h = SCENERY.cell / 2;
    const landAt = (k) => {
        const x = cellCentre(k % SCENERY.side);
        const z = cellCentre(Math.floor(k / SCENERY.side));
        return Math.max(height(x, z), height(x - h, z - h), height(x + h, z - h), height(x - h, z + h), height(x + h, z + h));
    };
    const cut = (layer) => {
        const low = layer.low.slice();
        const high = layer.high.slice();
        for (let k = 0; k < low.length; k++) {
            if (low[k] === SCENERY.empty)
                continue;
            if (high[k] * SCENERY.unit <= landAt(k) + LAND_SLACK) {
                low[k] = SCENERY.empty;
                high[k] = SCENERY.empty;
            }
        }
        return { low, high };
    };
    return { ...cut(grid), ...(grid.cover ? { cover: cut(grid.cover) } : {}) };
}
/**
 * Where the box [x0, x1] × [y0, y1] × [z0, z1] meets scenery, ground cover included: the centre
 * (x, z) of the meeting cell nearest the middle, or undefined. `inside`, when given, keeps only the
 * cells it accepts (a turned footprint). `coverTallest`, when given, passes over a cell's ground cover
 * no taller than that, metres (a library model stands in grass); taller cover counts as scenery.
 */
export function sceneryMeets(grid, x0, z0, x1, z1, y0, y1, inside, coverTallest) {
    const column = (v) => Math.floor((v + SCENERY.half) / SCENERY.cell);
    const meets = (layer, k) => layer.low[k] !== SCENERY.empty &&
        layer.low[k] * SCENERY.unit < y1 - 1e-6 &&
        layer.high[k] * SCENERY.unit > y0 + 1e-6;
    const cover = grid.cover;
    const coverMeets = (k) => cover !== undefined &&
        meets(cover, k) &&
        (coverTallest === undefined ||
            (cover.high[k] - cover.low[k]) * SCENERY.unit > coverTallest + 1e-6);
    const i0 = Math.max(0, column(x0));
    const i1 = Math.min(SCENERY.side - 1, column(x1));
    const j0 = Math.max(0, column(z0));
    const j1 = Math.min(SCENERY.side - 1, column(z1));
    let best;
    let nearest = Infinity;
    for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
            const k = j * SCENERY.side + i;
            if (!meets(grid, k) && !coverMeets(k))
                continue;
            const x = cellCentre(i);
            const z = cellCentre(j);
            if (inside && !inside(x, z))
                continue;
            const r = Math.hypot(x, z);
            if (r < nearest) {
                nearest = r;
                best = [x, z];
            }
        }
    }
    return best;
}
/** The boxes of the meshes whose names start with `prefix`, in all of a package's GLBs. */
function partBoxes(pkg, measured, prefix) {
    const out = [];
    for (const file of pkg.terrain.glb)
        for (const mesh of lookup(measured?.glbs ?? {}, file)?.meshes ?? [])
            if (mesh.name.startsWith(prefix) && mesh.bounds)
                out.push({ min: [...mesh.bounds.min], max: [...mesh.bounds.max] });
    return out;
}
/**
 * How far a `draaien` block's part reaches from its pivot as the platform counts it: to the farthest
 * corner of its meshes' boxes (a sphere, the safe side of any axis; stage.ts's clear-space rule counts
 * the same). 0 when nothing of the part was measured. The kit prints it beside the reach of the part's
 * farthest point (ADR-099).
 */
export function turningReach(pkg, measured, block) {
    let reach = 0;
    for (const b of partBoxes(pkg, measured, block.part))
        for (const x of [b.min[0], b.max[0]])
            for (const y of [b.min[1], b.max[1]])
                for (const z of [b.min[2], b.max[2]])
                    reach = Math.max(reach, Math.hypot(x - block.pivot[0], y - block.pivot[1], z - block.pivot[2]));
    return reach;
}
/**
 * A package's scenery (ADR-061): the measured grid (`complete` false when it was measured before
 * closer worlds, or unreadable: only what the package itself says is known), its props' boxes, each
 * route's lane (its vehicle's half-size round the path, every 25 cm) and each turning part's reach.
 */
export function packageScenery(pkg, measured) {
    let grid = emptyScenery();
    let complete = false;
    if (typeof measured?.scenery === 'string') {
        try {
            grid = decodeScenery(measured.scenery);
            complete = true;
        }
        catch {
            // Stored JSON of another shape: unknown, as before closer worlds.
        }
    }
    for (const prop of packageExtras(pkg).props) {
        for (const place of prop.at) {
            const box = propBox(prop, place);
            if (box)
                markScenery(grid, box.min[0], box.min[2], box.max[0], box.max[2], box.min[1], box.max[1]);
        }
    }
    for (const block of pkg.blocks) {
        if (block.type === 'route') {
            const boxes = partBoxes(pkg, measured, block.part);
            if (boxes.length === 0)
                continue;
            let half = 0;
            let y0 = Infinity;
            let y1 = -Infinity;
            for (const b of boxes) {
                half = Math.max(half, (b.max[0] - b.min[0]) / 2, (b.max[2] - b.min[2]) / 2);
                y0 = Math.min(y0, b.min[1]);
                y1 = Math.max(y1, b.max[1]);
            }
            // The headset sets every copy at the route's height (blocks/route.ts: the part's own shape is
            // modelled round y 0), so its lane is that high, not where the part lies in the GLB (ADR-099).
            y0 += block.y;
            y1 += block.y;
            const points = block.path;
            const last = block.closed ? points.length : points.length - 1;
            for (let s = 0; s < last; s++) {
                const [ax, az] = points[s];
                const [bx, bz] = points[(s + 1) % points.length];
                const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / 0.25));
                for (let t = 0; t <= steps; t++) {
                    const x = ax + ((bx - ax) * t) / steps;
                    const z = az + ((bz - az) * t) / steps;
                    markScenery(grid, x - half, z - half, x + half, z + half, y0, y1);
                }
            }
        }
        else if (block.type === 'draaien') {
            const reach = turningReach(pkg, measured, block);
            if (reach > 0) {
                const [px, py, pz] = block.pivot;
                markScenery(grid, px - reach, pz - reach, px + reach, pz + reach, py - reach, py + reach);
            }
        }
    }
    return { scenery: grid, complete };
}
/** The eight directions the prompt names, degrees from straight ahead (−z), right positive. */
export const OPEN_DIRECTIONS = [
    ['ahead', 0],
    ['ahead-right', 45],
    ['right', 90],
    ['behind-right', 135],
    ['behind', 180],
    ['behind-left', -135],
    ['left', -90],
    ['ahead-left', -45],
];
/**
 * How far the open ground reaches from the middle in each of eight directions (ADR-061): to the first
 * cell with scenery below `below` metres, to half a metre, `far` meaning "that far or more". What the
 * generator's prompt says about where things may go.
 */
export function openSpace(grid, below = 3, far = 12) {
    return OPEN_DIRECTIONS.map(([direction, azimuth]) => {
        const a = (azimuth * Math.PI) / 180;
        let distance = far;
        for (let r = 0; r <= far; r += 0.25) {
            const x = r * Math.sin(a);
            const z = -r * Math.cos(a);
            if (sceneryMeets(grid, x, z, x, z, 0, below) !== undefined) {
                distance = Math.max(0, Math.floor((r - 0.25) * 2) / 2);
                break;
            }
        }
        return { direction, azimuth, distance };
    });
}
/** Whether `grid` holds anything nearer the middle than the clear radius below its height (a check of the world, not of a lesson). */
export function sceneryInClear(grid) {
    return (sceneryMeets(grid, -STAGE.clear, -STAGE.clear, STAGE.clear, STAGE.clear, STAGE.apronHeight, STAGE.clearHeight, (x, z) => Math.hypot(x, z) < STAGE.clear - SCENERY.cell) !== undefined);
}
//# sourceMappingURL=scenery.js.map